import React, { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, Lock, MapPin } from 'lucide-react'
import { BottomSheet } from '../shell/BottomSheet'
import { useShellMode } from '../shell/useShellMode'
import { useOptionalEinsatz } from './EinsatzContext'
import { EinsatzOption, einsatzStatusInfo } from './helpers'

export const EINSATZ_WAHL_OEFFNEN = 'einsatz:wahl-oeffnen'
/** Öffnet die EinsatzWahl im Header (z. B. aus einem Hinweis oder einer leeren Seite). */
export function oeffneEinsatzWahl() {
  window.dispatchEvent(new CustomEvent(EINSATZ_WAHL_OEFFNEN))
}

export interface EinsatzWahlProps {
  /** Die aktuelle Seite braucht einen Einsatz: ohne Wahl wird der Chip in Warnfarbe gezeigt. */
  braucht?: boolean
  /** Bei höchstens einem Kandidaten kein Dropdown, nur der Chip. */
  hideWhenSingle?: boolean
  className?: string
}

function StatusTag({ e }: { e: EinsatzOption }) {
  const info = einsatzStatusInfo(e.status)
  if (!info.label) return null
  const cls = info.ton === 'success' ? 'ui-tag--success' : info.ton === 'info' ? 'ui-tag--info' : info.ton === 'warning' ? 'ui-tag--warning' : ''
  return <span className={`ui-tag ${cls}`}>{info.label}</span>
}

function Liste({ onDone }: { onDone: () => void }) {
  const ew = useOptionalEinsatz()!
  const pick = async (id: number | null) => { if (await ew.setEinsatz(id)) onDone() }
  return (
    <div className="ui-ew-list" role="listbox" aria-label="Einsatz wählen">
      {ew.fehler && (
        <div className="ui-ew-err" role="alert">
          {ew.fehler} <button type="button" className="ui-ew-link" onClick={() => void ew.reload()}>Erneut versuchen</button>
        </div>
      )}
      {ew.allowNone && (
        <button type="button" role="option" aria-selected={ew.einsatzId === null} className="ui-ew-it" onClick={() => void pick(null)}>
          <span className="ui-ew-it-main"><span className="ui-ew-it-t">Kein Einsatz</span></span>
          {ew.einsatzId === null && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
        </button>
      )}
      {ew.einsaetze.map(e => (
        <button key={e.id} type="button" role="option" aria-selected={e.id === ew.einsatzId} className="ui-ew-it" onClick={() => void pick(e.id)}>
          <span className="ui-ew-it-main">
            <span className="ui-ew-it-t">{e.titel}</span>
            {(e.typ || e.adresse) && <span className="ui-ew-it-s">{[e.typ, e.adresse].filter(Boolean).join(' · ')}</span>}
          </span>
          <StatusTag e={e} />
          {e.id === ew.einsatzId && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
        </button>
      ))}
      {!ew.einsaetze.length && !ew.fehler && <div className="ui-ew-empty">{ew.loading ? 'Einsätze werden geladen …' : 'Keine Einsätze verfügbar.'}</div>}
    </div>
  )
}

/**
 * Einsatzwahl für den Header (Slot `einsatzWahl` der AppShell).
 * PC/Tablet: Chip mit Status-Punkt, Antippen öffnet ein Dropdown. Handy: kompakter Chip (bleibt in der Kopfzeile sichtbar),
 * Antippen öffnet ein BottomSheet mit der Liste. Ohne EinsatzProvider wird nichts gerendert.
 */
export function EinsatzWahl({ braucht = false, hideWhenSingle = false, className }: EinsatzWahlProps) {
  const ew = useOptionalEinsatz()
  const { mode } = useShellMode()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const phone = mode === 'phone'

  useEffect(() => {
    const on = () => setOpen(true)
    window.addEventListener(EINSATZ_WAHL_OEFFNEN, on)
    return () => window.removeEventListener(EINSATZ_WAHL_OEFFNEN, on)
  }, [])

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => { if (!phone && root.current && !root.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); btn.current?.focus() } }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open, phone])

  if (!ew) return null
  const { einsatz } = ew
  const single = hideWhenSingle && ew.einsaetze.length <= 1 && !!einsatz
  const interactive = !ew.locked && !single
  const info = einsatzStatusInfo(einsatz?.status)
  const warn = braucht && !einsatz && !ew.loading
  const text = einsatz ? einsatz.titel : ew.loading ? 'Einsätze …' : 'Kein Einsatz'
  const tone = einsatz ? info.ton : 'none'

  return (
    <div ref={root} className={`ui-ew ${className ?? ''}`}>
      <button
        ref={btn}
        type="button"
        className="ui-ew-btn"
        data-warn={warn ? 'true' : undefined}
        data-tone={tone}
        disabled={!interactive}
        onClick={() => setOpen(o => !o)}
        aria-haspopup={phone ? 'dialog' : 'listbox'}
        aria-expanded={open}
        aria-label={`Einsatz: ${einsatz ? einsatz.titel : 'keiner gewählt'}${ew.locked ? ' (gesperrt)' : ''}`}
        title={ew.locked ? ew.lockedHint : einsatz ? einsatz.titel : undefined}
      >
        {einsatz ? <span className="ui-ew-dot" aria-hidden="true" /> : <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />}
        <span className="ui-ew-title">{text}</span>
        {!phone && einsatz && info.label && <span className="ui-ew-state">{info.label}</span>}
        {ew.locked ? <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : interactive && <ChevronDown className="ui-ew-chev h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
      </button>
      {open && !phone && interactive && (
        <div className="ui-ew-pop ui-pop animate-fade-in">
          <Liste onDone={() => { setOpen(false); btn.current?.focus() }} />
        </div>
      )}
      {phone && interactive && (
        <BottomSheet standalone label="Einsatz wählen" open={open} onClose={() => setOpen(false)}>
          <div className="ui-grp">Einsatz wählen</div>
          <Liste onDone={() => setOpen(false)} />
        </BottomSheet>
      )}
    </div>
  )
}
